import cn from "classnames";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { HANDBOOK_INITIAL_LOAD_COUNT, HANDBOOK_SHOW_MORE_LIMIT } from "pages/HandBook/const";
import HandBookAddButton from "pages/HandBook/features/HandBookAddButton/HandBookAddButton";
import { handBookActions } from "pages/HandBook/model/slice";
import { getHandbooks, getHandbooksMore } from "pages/HandBook/model/thunks";
import { HANDBOOK_EXPENDITURE_APPROVAL_STATUS, IHandBook, IHandBookRequest } from "pages/HandBook/model/types";
import { IHandbookFormField } from "pages/HandBook/ui/HandbookForm/HandbookForm";
import HandbookRow from "pages/HandBook/ui/HandbookRow/HandbookRow";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";

import icon from "./../../assets/handbook.svg";
import { useTypedSelector } from "app/store/typedUseSelector";
import TableReusableHead, { TableReusableHeaderCell } from "shared/ui/dataDisplay/TableReusable/TableReusableHead";
import Checkbox from "shared/ui/inputs/Checkbox/Checkbox";
import ListWrapper from "shared/ui/layout/ListWrapper/ListWrapper";
import RowGrid, { ROW_GRID_UNIT } from "shared/ui/layout/RowGrid/RowGrid";

import { generateStorageKey } from "utils/helpers/generateStorageKey";

import handBookStyles from "../../HandBook.module.scss";

interface IProps {
  defaultFilter?: IHandBookRequest;
  colPercentsHead?: number[];
  colPercents: number[];
  columns: IHandbookColumn[];
  formColPercents?: number[];
  fields: IHandbookFormField[];
  canAddMore?: boolean;
  canCheckRows?: boolean;
  tableHeadClassName?: string;
  beforeTableHeadColumns?: React.ReactNode;
}

export interface IHandbookColumn {
  header: {
    title: string;
    element?: React.ReactNode;
    filterName?: string;
    filterType?: "select";
    filterOptions?: any[];
  };
  isCentered?: boolean;
  isNoBreak?: boolean;
  isLast?: boolean;
  isRighted?: boolean;
  render?: (item: IHandBook) => ReactNode;
}

const HandBookList = ({
  defaultFilter = { status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED, ordering: "name" },
  colPercentsHead,
  colPercents,
  columns,
  formColPercents,
  fields,
  canAddMore,
  canCheckRows,
  tableHeadClassName,
}: IProps) => {
  const [filter, setFilter] = useState<IHandBookRequest>(defaultFilter);
  const dispatch = useDispatch();
  const controller = useRef(new AbortController());
  const key = generateStorageKey(filter as any);
  const [loadingMoreKey, setLoadingMoreKey] = useState<any>();
  const handbooks = useTypedSelector((state) => state.handBookReducer.handbooks)[key];
  const loadings = useTypedSelector((state) => state.handBookReducer.loadings);
  const isLoading = loadings[key];
  const isLoadingMore = Boolean(loadingMoreKey && loadings[loadingMoreKey]);
  const invalidationKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  const editings = useTypedSelector((state) => state.handBookReducer.editings);
  const isEditing = editings.isEditing;

  const displayHandbooks = useMemo(() => {
    if (!handbooks?.results) return handbooks?.results;
    return handbooksModifyWithEditings(handbooks.results, editings);
  }, [handbooks?.results, editings]);

  useEffect(() => {
    controller.current.abort();
    controller.current = new AbortController();
    dispatch(getHandbooks({ ...filter, limit: HANDBOOK_INITIAL_LOAD_COUNT }, controller.current));
  }, [invalidationKey, filter]);

  useEffect(() => {
    return () => {
      dispatch(handBookActions.clearLocalAddings());
    };
  }, []);

  const onPagination = () => {
    const loadingMoreFilters = { ...filter, offset: handbooks?.results?.length, limit: HANDBOOK_SHOW_MORE_LIMIT };
    setLoadingMoreKey(generateStorageKey(loadingMoreFilters as any));
    dispatch(getHandbooksMore(loadingMoreFilters));
  };

  const showCheckAll = isEditing || canCheckRows;

  const headerPercentsBase = colPercentsHead || colPercents;
  const headerPercents = showCheckAll
    ? [3, headerPercentsBase[0] - 3, ...headerPercentsBase.slice(1)]
    : headerPercentsBase;

  const allChecked = useMemo(() => {
    if (!displayHandbooks || displayHandbooks.length === 0) return false;
    return displayHandbooks.every((item) => editings.checked[item.id]);
  }, [displayHandbooks, editings.checked]);

  const handleToggleAllChecked = useCallback(() => {
    if (!displayHandbooks || displayHandbooks.length === 0) return;

    const newCheckedState = !allChecked;

    displayHandbooks.forEach((item) => {
      if (Boolean(editings.checked[item.id]) !== newCheckedState) {
        dispatch(handBookActions.localCheckItem({ id: item.id, isChecked: newCheckedState }));
      }
    });
  }, [displayHandbooks, allChecked, editings.checked, dispatch]);

  return (
    <>
      <ListWrapper
        filter={filter}
        onPagination={onPagination}
        paginationLimit={HANDBOOK_SHOW_MORE_LIMIT}
        total={handbooks?.count}
        header={
          <TableReusableHead
            isSticky
            isNoDefaultGridCols
            className={cn(showCheckAll && handBookStyles.headerWithCheckAll, tableHeadClassName)}
          >
            <RowGrid percents={headerPercents} unit={ROW_GRID_UNIT.PERCENT}>
              {showCheckAll ? (
                <TableReusableHeaderCell isCentered>
                  <Checkbox onCheck={handleToggleAllChecked} checked={allChecked} />
                </TableReusableHeaderCell>
              ) : null}
              {columns.map((el) => (
                <TableReusableHeaderCell
                  key={el.header.title}
                  isCentered={el.isCentered}
                  filterType={el.header.filterType ? el.header.filterType : el.header.filterName ? "text" : undefined}
                  filterField={el.header.filterName}
                  onChangeFilter={(name, value) => setFilter((prev) => ({ ...defaultFilter, ...prev, [name]: value }))}
                  filterTitle={el.header.title}
                  isLast={el.isLast}
                  filterOptions={el.header.filterOptions}
                  isRighted={el.isRighted}
                  isDebouncedFilterApply
                >
                  {el.header.title}
                  {el.header.element}
                </TableReusableHeaderCell>
              ))}
            </RowGrid>
          </TableReusableHead>
        }
        data={displayHandbooks}
        dataMapper={(item) => (
          <HandbookRow
            key={item.id}
            item={item}
            colPercents={colPercents}
            columns={columns}
            formColPercents={formColPercents}
            fields={fields}
            canCheck={canCheckRows}
          />
        )}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        placeholderProps={{ img: icon }}
      />
      {canAddMore && !isLoading && <HandBookAddButton />}
    </>
  );
};

export default HandBookList;
